import {Color} from '@benchling/nucleus-ui';
import {
  Box,
  type CSSObject,
  Group,
  Highlight,
  Loader,
  Text,
  UnstyledButton,
  createStyles,
} from '@mantine/core';
import React from 'react';

import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import {
  AI_CHAT_ACCENT_COLOR,
  getObjectTypeDisplayName,
  RECENT_SEARCH_CHAT_OBJECT_TYPE,
} from 'omnibox/omniboxConstants';
import {
  GRAY,
  GREEN,
  type IconConfig,
  MODEL_TYPE_TO_ICON,
  ORANGE,
  getColorForModelType,
} from 'omnibox/omniboxTypeIcons';
import type {ResultType} from 'omnibox/omniboxTypes';
import {useSmoothedText} from 'omnibox/useSmoothedText';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil, {apiIdToIconName} from 'util/APIUtil';

export interface OmniboxActionItem {
  id: string;
  title: string;
  description?: string;
  // A second muted line below the subtitle, e.g. a content-match snippet.
  snippet?: string;
  group?: string;
  resultType?: ResultType | undefined;
  apiId?: string;
  entryApiId?: string;
  objectType?: string;
  modifiedAt?: string | null;
  closeOnTrigger?: boolean;
  onTrigger: () => void;
}

interface OmniboxActionProps {
  action: OmniboxActionItem;
  hovered: boolean;
  // The typed search text; the matching part of a result's title is emphasized.
  query?: string;
  onTrigger: () => void;
  // When set, renders a "Start AI chat" link on hover; omit to suppress it (rows without an item).
  onStartAiChat?: () => void;
}

// Maps domain graph class names (returned by backend) to human-friendly display names.
// The backend hydrates objectType via model_type_name_or_unknown() which returns class names
// like "DnaSequence", while recently-viewed items use display names like "DNA Sequence".
// See also MODEL_TYPE_DISPLAY_NAMES in omniboxConstants.ts which maps ModelTypeConstants
// enum values (snake_case) to display names for API-ID-based resolution.
const DOMAIN_CLASS_TO_DISPLAY_NAME: Record<string, string> = {
  DnaSequence: 'DNA Sequence',
  RnaSequence: 'RNA Sequence',
  DnaOligo: 'DNA Oligo',
  RnaOligo: 'RNA Oligo',
  AaSequence: 'Protein',
  BasicFolderItem: 'Custom Entity',
  MatrixPlate: 'Plate',
  WellPlate: 'Plate',
  Process: 'Workflow',
  ProcessTask: 'Workflow Task',
  ProcessOutput: 'Workflow Output',
  StageEntry: 'Stage Entry',
  AssayRun: 'Run',
  DatasetTable: 'Dataset',
  FileTable: 'File',
  StudyTable: 'Study',
  AnalysisTemplateTable: 'Automation Template',
  TemplateCollection: 'Template Collection',
  RequestV2SubmissionTable: 'Request',
  SequenceAnalysis: 'DNA/RNA Alignment',
  ProteinAlignment: 'AA Alignment',
  AnalyticsDashboard: 'Dashboard',
  BulkAssembly: 'Assembly',
  WorksheetTable: 'Worksheet',
  CanvasFolderArtifact: 'Artifact',
  RequestV2Definition: 'Request Template',
  EntrySubtemplate: 'Sub-template',
  FormDefinition: 'Form Template',
  PlateDesignTemplate: 'Plate Design Template',
  TestDefinition: 'Test Definition',
  TestOrder: 'Test Order',
};

export function normalizeObjectType(objectType: string | undefined): string | undefined {
  if (!objectType) {
    return undefined;
  }
  return DOMAIN_CLASS_TO_DISPLAY_NAME[objectType] ?? objectType;
}

function getIconForApiId(apiId: string | undefined, objectType: string | undefined): IconConfig {
  if (apiId) {
    const modelType = APIUtil.apiIdToMaybeType(apiId);
    if (modelType) {
      const icon = MODEL_TYPE_TO_ICON[modelType] ?? apiIdToIconName(apiId);
      return {icon, ...getColorForModelType(modelType)};
    }
  }
  // Fallback for items without a recognized apiId
  return {icon: 'search', ...GRAY};
}

function getIconForResultType(resultType: string | undefined): IconConfig {
  switch (resultType) {
    case 'help_docs_article':
    case 'help_docs_loading':
      return {icon: 'study', bgColor: Color.RED_100, color: Color.RED_500};
    case 'advanced_search':
      return {icon: 'search', ...GRAY};
    case 'content':
      return {icon: 'paperclip', bgColor: Color.GRAY_100, color: Color.GRAY_600};
    case 'ask_suggestion':
      return {icon: 'sparkle', bgColor: Color.BLUE_100, color: Color.BRAND_ROYAL};
    case 'saved_search':
      return {icon: 'search', ...ORANGE};
    case 'assigned_task_status':
      return {icon: 'done', ...GREEN};
    default:
      return {icon: 'search', ...GRAY};
  }
}

const useStyles = createStyles((theme) => ({
  resultIcon: {
    width: 32,
    height: 32,
    gap: 10,
    borderRadius: 4,
    padding: theme.spacing.xs,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  resultIconInner: {
    fontSize: 20,
    width: 20,
    height: 20,
  },
  // The advanced-search escalation and saved-search rows use a smaller glyph within the same square.
  resultIconInnerSmall: {
    fontSize: 16,
    width: 16,
    height: 16,
  },
  directAnswerTitle: {
    fontSize: 14,
    fontWeight: 500,
  },
  resultTitle: {
    lineHeight: 1,
  },
  resultDescription: {
    lineHeight: '14px',
    color: theme.colors.gray[5],
  },
  resultSnippet: {
    lineHeight: '14px',
    color: theme.colors.gray[5],
    fontStyle: 'italic' as const,
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 16px',
  },
  resultInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 32,
    flex: 1,
    minWidth: 0,
  },
  resultContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    flex: 1,
    overflow: 'hidden',
    minWidth: 0,
  },
  modifiedAt: {
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
  },
  aiChatButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    flexShrink: 0,
    height: 20.5,
    padding: `${theme.spacing.xxs} ${theme.spacing.sm}`,
    fontFamily: theme.fontFamily,
    fontWeight: 600,
    fontSize: theme.fontSizes.sm,
    lineHeight: '16.5px',
    whiteSpace: 'nowrap' as const,
    color: theme.colors.grape[6],
  },
  aiChatButtonIcon: {
    fontSize: 10,
    width: 10,
    height: 10,
    display: 'block',
  },
}));

function ResultIcon({iconConfig, small = false}: {iconConfig: IconConfig; small?: boolean}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div
      className={classes.resultIcon}
      style={{backgroundColor: iconConfig.bgColor, color: iconConfig.color}}
      aria-hidden="true"
    >
      <BenchlingIcon
        icon={iconConfig.icon}
        className={small ? classes.resultIconInnerSmall : classes.resultIconInner}
      />
    </div>
  );
}

// --- Query match highlighting ---

// Rows whose title is a matched object name. Rows that echo the query back (advanced search, ask
// suggestions) are excluded since every one would trivially "match".
const TITLE_HIGHLIGHT_RESULT_TYPES: ReadonlySet<ResultType | undefined> = new Set<ResultType>([
  'search_result',
  'saved_search',
  'content',
  'help_docs_article',
]);

const TITLE_MATCH_STYLES: CSSObject = {
  backgroundColor: 'transparent',
  color: Color.BLUE_500,
  fontWeight: 700,
};

// --- Date formatting ---

const relativeFormatter = new Intl.RelativeTimeFormat('en', {numeric: 'auto', style: 'narrow'});

function formatRelativeDate(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffSeconds / 3600);
  const diffDays = Math.round(diffSeconds / 86400);

  if (Math.abs(diffMinutes) < 1) {
    return 'just now';
  }
  if (Math.abs(diffHours) < 1) {
    return relativeFormatter.format(diffMinutes, 'minute');
  }
  if (Math.abs(diffDays) < 1) {
    return relativeFormatter.format(diffHours, 'hour');
  }
  if (Math.abs(diffDays) < 7) {
    return relativeFormatter.format(diffDays, 'day');
  }
  const month = date.toLocaleDateString('en-US', {month: 'short'});
  const day = date.getDate();
  if (date.getFullYear() === now.getFullYear()) {
    return `${month} ${day}`;
  }
  return `${month} ${day}, ${date.getFullYear()}`;
}

function formatAbsoluteDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// --- Main component ---

function DirectAnswerTitle({markdown}: {markdown: string}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  // The backend flushes the answer in ~200ms bursts; smooth it so the text flows in word-by-word
  // instead of jumping several words at a time.
  const smoothedMarkdown = useSmoothedText(markdown);
  return (
    <div className={`${classes.directAnswerTitle} omnibox-answer-markdown`}>
      <LLMMarkdownSection markdown={smoothedMarkdown} />
    </div>
  );
}

export default function OmniboxAction({
  action,
  hovered,
  query = '',
  onTrigger,
  onStartAiChat,
}: OmniboxActionProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const resultType = action.resultType;
  const objectType = action.objectType;

  // Direct answer gets special full-width rendering
  if (resultType === 'direct_answer') {
    return (
      <Box
        data-hovered={hovered || undefined}
        onClick={onTrigger}
        sx={(theme) => ({
          display: 'block',
          width: '100%',
          padding: '10px 16px',
          borderRadius: theme.radius.sm,
          cursor: 'pointer',
          transition: 'background-color 100ms ease',
          backgroundColor: theme.colors.gray[0],
          ...(hovered ? {backgroundColor: theme.colors.gray[1]} : {}),
          '&:hover': {backgroundColor: theme.colors.gray[1]},
          '& .omnibox-answer-markdown *': {margin: '0 !important', padding: 0},
        })}
      >
        <DirectAnswerTitle markdown={action.title} />
        {action.description && (
          <Text color="dimmed" size="xs" mt={4}>
            {action.description}
          </Text>
        )}
      </Box>
    );
  }

  // Loading states
  if (resultType === 'help_docs_loading') {
    const iconConfig = getIconForResultType(resultType);
    return (
      <div className={classes.loadingRow}>
        <ResultIcon iconConfig={iconConfig} />
        <Group noWrap={true} spacing={8} sx={{flex: 1}}>
          <Loader size="xs" />
          <Text size="sm" weight={500} color="dimmed" lineClamp={1} sx={{flex: 1}}>
            {action.title}
          </Text>
        </Group>
      </div>
    );
  }

  // Recent search rows — compact single-line rows matching the Figma design
  if (resultType === 'recent_search') {
    const hasOpenedItem = Boolean(action.apiId);
    const isChat = hasOpenedItem && objectType === RECENT_SEARCH_CHAT_OBJECT_TYPE;
    const iconColor = isChat ? AI_CHAT_ACCENT_COLOR : Color.GRAY_600;
    let icon: BenchlingIconName = 'clock-outline';
    if (isChat) {
      icon = 'wand-magic-sparkles';
    } else if (hasOpenedItem) {
      icon = getIconForApiId(action.apiId, objectType).icon;
    }
    return (
      <Box
        data-hovered={hovered || undefined}
        onClick={onTrigger}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '4px 16px',
          minHeight: 27.5,
          borderRadius: theme.radius.sm,
          cursor: 'pointer',
          transition: 'background-color 100ms ease',
          ...(hovered ? {backgroundColor: theme.colors.gray[1]} : {}),
          '&:hover': {backgroundColor: theme.colors.gray[0]},
          '&:hover[data-hovered]': {backgroundColor: theme.colors.gray[1]},
        })}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            flexShrink: 0,
          }}
        >
          <BenchlingIcon icon={icon} style={{fontSize: 13, color: iconColor}} aria-hidden="true" />
        </span>
        <Text size="sm" weight={400} lineClamp={1} sx={{flex: 1}}>
          {action.title}
        </Text>
        {isChat && (
          <Text size="sm" color={AI_CHAT_ACCENT_COLOR}>
            Go to chat
          </Text>
        )}
      </Box>
    );
  }

  // All other results — card-like rows with icon + title + subtitle. The advanced-search
  // escalation row also renders here: a grey magnifying-glass icon (see getIconForResultType)
  // plus its title, with no subtitle.
  const useApiIdIcon =
    resultType === 'search_result' ||
    resultType === 'recent_item' ||
    (resultType === 'content' && action.apiId);
  const iconConfig = useApiIdIcon
    ? getIconForApiId(action.apiId, objectType)
    : getIconForResultType(resultType);

  // The backend sends the literal "unknown" when it can't resolve a result's type — e.g. legacy
  // types like Protocol whose api-id prefix isn't registered in the domain graph. Rather than
  // surface that sentinel, fall back to deriving the type from the api-id prefix, which the client
  // can resolve (the same prefix map drives the result icon).
  const normalizedObjectType = normalizeObjectType(objectType);
  const subtitle =
    resultType === 'saved_search'
      ? 'Saved search'
      : (normalizedObjectType === 'unknown' ? undefined : normalizedObjectType) ||
        (action.apiId ? getObjectTypeDisplayName(action.apiId) : undefined) ||
        (resultType === 'content' ? (action.apiId ? 'Entry' : 'Attachment') : undefined);

  const secondaryText = action.description;
  const modifiedAt = action.modifiedAt;

  return (
    <Box
      data-hovered={hovered || undefined}
      onClick={onTrigger}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.sm,
        width: '100%',
        minHeight: 48,
        padding: `${theme.spacing.xxs} ${theme.spacing.lg}`,
        borderRadius: theme.radius.sm,
        cursor: 'pointer',
        transition: 'background-color 100ms ease',
        ...(hovered ? {backgroundColor: theme.colors.blue[0]} : {}),
        '&:hover': {backgroundColor: theme.colors.blue[0]},
        '&:hover[data-hovered]': {backgroundColor: theme.colors.blue[0]},
      })}
    >
      <div className={classes.resultInner}>
        {/* The saved-search and advanced-search glyphs read better a touch smaller than entity icons. */}
        <ResultIcon
          iconConfig={iconConfig}
          small={resultType === 'saved_search' || resultType === 'advanced_search'}
        />
        <div className={classes.resultContent}>
          <Highlight
            size="md"
            weight={600}
            lineClamp={1}
            className={classes.resultTitle}
            highlight={TITLE_HIGHLIGHT_RESULT_TYPES.has(resultType) ? query : ''}
            highlightStyles={TITLE_MATCH_STYLES}
          >
            {action.title}
          </Highlight>
          {(subtitle || secondaryText) && (
            <Text size="sm" lineClamp={1} className={classes.resultDescription}>
              {subtitle}
              {subtitle && secondaryText ? '  ·  ' : ''}
              {secondaryText}
            </Text>
          )}
          {action.snippet && (
            <Text size="sm" lineClamp={1} className={classes.resultSnippet}>
              {action.snippet}
            </Text>
          )}
        </div>
      </div>
      {hovered && onStartAiChat ? (
        <UnstyledButton
          type="button"
          className={classes.aiChatButton}
          // Keep the row's combobox/listbox keyboard model intact: don't take focus on click
          // (the row handles focus), and stay out of the tab order.
          tabIndex={-1}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={(event) => {
            event.stopPropagation();
            onStartAiChat();
          }}
        >
          <BenchlingIcon icon="magic-wand" className={classes.aiChatButtonIcon} aria-hidden="true" />
          Start AI chat
        </UnstyledButton>
      ) : (
        modifiedAt && (
          <Text
            size="xs"
            color="dimmed"
            className={classes.modifiedAt}
            title={formatAbsoluteDate(modifiedAt)}
          >
            {formatRelativeDate(modifiedAt)}
          </Text>
        )
      )}
    </Box>
  );
}
